import { useChartTimeNavigation } from './ChartTimeNavigation'
import { memo, useMemo, useRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import type { ECharts, EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCandle, ScreenerOIDataPoint } from '@/types'
import { ChartRuler, type OIRulerSeries, type RulerExchangeSeries } from './ChartRuler'
import { ChartCollapseButton } from './ChartCollapseButton'
import { ChartLevelAlerts, chartAlertMarkLine, type ChartAlertControls } from './ChartLevelAlerts'
import { useChartAlertDrag } from './useChartAlertDrag'
import { useVisibleChartScale } from './useVisibleChartScale'
import { useChartResize } from './useChartResize'
import { chartTooltipTime, useChartCrosshair } from './useChartCrosshair'
import {
  findLastKnownIndex,
  formatAge,
  formatUtc,
  formatUtcAxis,
  isPlottableOI,
} from './chartUtils'

interface PriceLineChartProps {
  oiData: ScreenerOIDataPoint[]
  candles: ScreenerCandle[]
  currentPrice?: number | null
  candleInterval?: string
  symbol: string
  exchange: string
  interval: string
  chartGroup: string
  onCrosshairMove: (time: Time | null) => void
  syncedTime: Time | null
  height?: number
  alertControls?: ChartAlertControls
  market?: 'spot' | 'futures'
  rulerExchanges?: RulerExchangeSeries[]
  collapsed?: boolean
  onToggleCollapse?: () => void
}

function formatPrice(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: value >= 100 ? 2 : 4,
    maximumFractionDigits: value >= 1 ? 6 : 8,
  })
}

function PriceLineChartComponent({
  oiData,
  candles,
  currentPrice,
  candleInterval,
  symbol,
  exchange,
  interval,
  chartGroup,
  onCrosshairMove,
  syncedTime,
  height = 330,
  alertControls: savedAlertControls,
  market = 'futures',
  rulerExchanges,
  collapsed = false,
  onToggleCollapse,
}: PriceLineChartProps) {
  const chartRef = useRef<ReactECharts>(null)
  const [rulerChart, setRulerChart] = useState<ECharts | null>(null)
  useChartResize(rulerChart)
  const timeNavigation = useChartTimeNavigation(rulerChart)

  const derived = useMemo(() => {
    const aligned = oiData
      .filter((point) => isPlottableOI(point) && Number.isFinite(point.timestamp)
        && Number.isFinite(point.price) && Number(point.price) > 0)
      .map((point) => ({ timestamp: point.timestamp, price: Number(point.price), source: 'OI aligned' }))
    const fallback = candles
      .filter((candle) => Number.isFinite(candle.timestamp) && Number.isFinite(candle.close) && candle.close > 0)
      .map((candle) => ({ timestamp: candle.timestamp, price: Number(candle.close), source: 'Candle close' }))
    const points = (aligned.length > 1 || fallback.length === 0 ? aligned : fallback)
      .sort((a, b) => a.timestamp - b.timestamp)
    const timestamps = points.map((point) => point.timestamp * 1000)
    return { points, timestamps, source: points[0]?.source || 'No data' }
  }, [oiData, candles])
  const alertMarket = derived.source === 'OI aligned' ? 'futures' : market
  const alertControls = useChartAlertDrag(rulerChart, savedAlertControls, {
    symbol, metric: 'price', market: alertMarket, exchanges: [exchange],
  })

  const scaleSeries = useMemo(() => [derived.points.map((point) =>
    [point.timestamp * 1000, point.price] as const)], [derived.points])
  const alertSeries = useMemo(() => [{ exchange, points: scaleSeries[0] }], [exchange, scaleSeries])
  const { fitVisibleScale, onChartReady } = useVisibleChartScale(scaleSeries, `${symbol}:${exchange}:${interval}`, true)

  const rulerSeries = useMemo<OIRulerSeries[]>(() => [{
    id: exchange,
    label: exchange,
    color: '#2dd4bf',
    points: derived.points.map((point) => ({ timestamp: point.timestamp, value: point.price })),
    oiData,
    candles,
    currentPrice,
  }], [exchange, derived.points, oiData, candles, currentPrice])

  const selected = useMemo(() => {
    if (derived.points.length === 0) return { point: null, age: null }
    if (syncedTime == null) return { point: derived.points[derived.points.length - 1], age: null }
    const targetMs = Number(syncedTime) * 1000
    const index = findLastKnownIndex(derived.timestamps, targetMs)
    if (index < 0) return { point: null, age: null }
    const point = derived.points[index]
    return { point, age: Math.max(0, Math.floor(Number(syncedTime) - point.timestamp)) }
  }, [derived, syncedTime])

  const option = useMemo<EChartsOption>(() => ({
    animation: false,
    useUTC: true,
    backgroundColor: 'transparent',
    grid: { left: 10, right: 76, top: 42, bottom: 34, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', lineStyle: { color: '#64748b', type: 'dashed' } },
      backgroundColor: 'rgba(5, 10, 18, 0.96)',
      borderColor: '#334155',
      textStyle: { color: '#e2e8f0', fontSize: 11 },
      formatter: (raw: any) => {
        const entries = Array.isArray(raw) ? raw : [raw]
        const axisMs = chartTooltipTime(chartRef.current?.getEchartsInstance(), Number(entries[0]?.axisValue ?? 0))
        const index = findLastKnownIndex(derived.timestamps, axisMs)
        const point = index >= 0 ? derived.points[index] : null
        if (!point) return `<div>${formatUtc(axisMs, interval === '5s')}</div><div>Price: N/A</div>`
        const age = Math.max(0, Math.floor((axisMs - point.timestamp * 1000) / 1000))
        return `
          <div style="color:#94a3b8;margin-bottom:5px">${formatUtc(axisMs, interval === '5s')}</div>
          <div>${exchange.toUpperCase()} Price: <b style="color:#2dd4bf">$${formatPrice(point.price)}</b></div>
          ${age > 0 ? `<div style="color:#f59e0b;font-size:10px">Last sample ${formatAge(age)}</div>` : ''}
        `
      },
    },
    xAxis: {
      type: 'time',
      axisPointer: { snap: false },
      ...timeNavigation.axis,
      axisLine: { lineStyle: { color: '#253044' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        hideOverlap: true,
        formatter: (value: number) => formatUtcAxis(value, interval),
      },
      splitLine: { show: true, lineStyle: { color: '#172033', type: 'dashed' } },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      triggerEvent: true,
      scale: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        formatter: (value: number) => formatPrice(value),
      },
      splitLine: { lineStyle: { color: '#172033', type: 'dashed' } },
    },
    dataZoom: [
      {
        type: 'inside',
        id: 'screener-time',
        ...timeNavigation.zoom,
        xAxisIndex: [0],
        zoomOnMouseWheel: true,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
        filterMode: 'none',
      },
      {
        type: 'inside',
        yAxisIndex: [0],
        id: 'screener-price-scale',
        zoomOnMouseWheel: 'shift',
        moveOnMouseMove: 'alt',
        moveOnMouseWheel: false,
        filterMode: 'none',
      },
    ],
    series: [{
      name: 'Price',
      type: 'line',
      showSymbol: derived.points.length === 1,
      smooth: false,
      connectNulls: false,
      lineStyle: { width: 1.5, color: '#2dd4bf' },
      itemStyle: { color: '#2dd4bf' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(45, 212, 191, 0.16)' },
            { offset: 1, color: 'rgba(45, 212, 191, 0.01)' },
          ],
        },
      },
      data: derived.points.map((point) => [point.timestamp * 1000, point.price]),
      markLine: chartAlertMarkLine(alertControls, symbol, exchange, 'price', alertMarket),
    }],
  }), [derived, exchange, interval, alertControls, symbol, alertMarket, timeNavigation])

  useChartCrosshair(rulerChart, chartGroup, onCrosshairMove, interval)

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-800/90 bg-[#080d16] shadow-[0_18px_50px_rgba(0,0,0,0.22)]" style={collapsed ? { height: 48 } : undefined}>
      <div className="pointer-events-none absolute left-4 top-3 z-10 flex items-center gap-3">
        {onToggleCollapse && <ChartCollapseButton collapsed={collapsed} onToggle={onToggleCollapse} label="price chart" />}
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Price</span>
        <span className="text-xs font-semibold uppercase text-slate-200">{symbol} · {exchange}</span>
        <span className="rounded-full border border-slate-700/70 bg-slate-900/80 px-2 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
          {derived.source}
        </span>
      </div>
      {selected.point && (
        <button type="button" onClick={fitVisibleScale}
          title="Click to fit price scale to the visible time range"
          aria-label="Fit price scale to visible time range"
          className="absolute right-3 top-3 z-10 flex cursor-pointer items-center gap-2 rounded-md border border-teal-400/20 bg-teal-400/10 px-2.5 py-1 hover:border-teal-300/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300">
          <span className="font-mono text-xs font-semibold text-teal-300">${formatPrice(selected.point.price)}</span>
          {selected.age != null && selected.age > 0 && (
            <span className="text-[9px] text-amber-400">{formatAge(selected.age)}</span>
          )}
        </button>
      )}
      <div style={collapsed ? { display: 'none' } : undefined} aria-hidden={collapsed || undefined}>
        <ReactECharts ref={chartRef} option={option} style={{ height, width: '100%' }} autoResize={false}
          onChartReady={(instance) => { instance.group = chartGroup; onChartReady(instance); setRulerChart(instance) }}
          replaceMerge={['series']} lazyUpdate />
      </div>
      {!collapsed && <ChartRuler chart={rulerChart} metric="price" series={rulerSeries} rulerExchanges={rulerExchanges} resetKey={`${symbol}:${exchange}:${interval}`}
        interval={interval} candleInterval={candleInterval} barInterval={derived.source === 'Candle close' ? candleInterval : interval} />}
      {!collapsed && <ChartLevelAlerts chart={rulerChart} metric="price" market={alertMarket} series={alertSeries} controls={alertControls} />}
    </div>
  )
}

export const PriceLineChart = memo(PriceLineChartComponent)
